/**
 * Araç hasar ve değer kaybı hesabını WORD belgesine döker.
 * İşçilik alacakları raporuyla aynı yöntem (bkz. iscilikWord.js):
 * Word, HTML tabanlı .doc dosyasını açar; Türkçe karakterler kusursuz çıkar.
 */
import { tl, yuzde, tarihTR, parcaBul, ISLEMLER, ONARIM_SEVIYELERI, BOYALAR, DAYANAKLAR, bugunISO } from './aracHasar';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const ad = (liste, id) => (liste.find((x) => x.id === id) || {}).ad || '';

export function aracHasarWordUret(sonuc, bilgi = {}) {
  const f = bilgi.form || {};
  const { hasar, dk, ek1, ek1Tahmin, mahrumiyet, ozet, temerrut, zamanasimi10, uyarilar = [] } = sonuc;
  // Emsal yokken değer kaybı rapordaki verilerle formülden tahmin edildi (dk.yontem === 'formul').
  const formulle = dk?.yontem === 'formul';
  const et = ek1Tahmin || ek1;
  const tahminParcalari = formulle
    ? (et.satirlar || []).filter((s) => s.toplam > 0).map((s) => `${s.ad} ${s.toplam.toLocaleString('tr-TR')}`).join(' · ')
    : '';
  // Emsalle hesaplandığında eski formül ayrıca karşılaştırma olarak verilebilir; formülle tahminde tekrar olurdu.
  const karsilastirma = f.ek1Acik && !formulle;

  const bilgiSatir = [
    ['Föy No', bilgi.foyNo || '-'],
    ['Müvekkil', bilgi.muvekkil || '-'],
    ['Plaka', f.plaka || '-'],
    ['Araç', [f.marka, f.model, f.modelYili].filter(Boolean).join(' ') || '-'],
    ['Araç grubu / kullanım', `${bilgi.grupAdi || '-'} / ${f.kullanim === 'hususi' ? 'Hususi' : f.kullanim === 'ticari' ? 'Ticari' : f.kullanim === 'kiralik' ? 'Kiralık' : 'Taksi'}`],
    ['Kaza tarihi', tarihTR(f.kazaTarihi)],
    ['Kaza tarihindeki km', f.km || '-'],
    ['Önceki hasar kaydı', `${f.sbmKayit || 0} kayıt${f.sbmTutar ? `, toplam ${f.sbmTutar} TL` : ''}${f.sbmAciklama ? ` (${f.sbmAciklama})` : ''}`],
    ['Karşı sigorta şirketi', f.karsiSigorta || bilgi.karsiSigorta || '-'],
    ['Karşı tarafın kusuru', `%${hasar.kusurOrani}`],
  ].map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td class="v">${esc(v)}</td></tr>`).join('');

  // Parça fiyatları bilinmiyor, hasar yalnız "parça + işçilik" toplamından: fiyat sütunu yok.
  const toplamGirisi = !!hasar.toplamGirisi;
  const parcaSatir = (f.parcalar || []).map((r) => {
    const p = parcaBul(r.kod) || {};
    const islem = [ad(ISLEMLER, r.islem), r.islem === 'onarim' ? ad(ONARIM_SEVIYELERI, r.onarimSeviyesi) : '', r.islem !== 'sokTak' ? ad(BOYALAR, r.boya) : '']
      .filter(Boolean).join(' · ');
    // Ekspertizden gelen satırlarda raporun kendi parça adı (r.ad) gösterilir.
    const adi = r.ad ? `${r.ad}${p.ad && r.kod !== 'X.25' ? ` (${p.ad})` : ''}` : (p.ad || r.kod);
    const fiyat = toplamGirisi ? ''
      : `<td class="s">${r.islem === 'degisim' && r.bedel ? `${esc(r.bedel)} TL${Number(r.adet) > 1 ? ` × ${esc(r.adet)}` : ''}` : '-'}</td>`;
    return `<tr><td>${esc(adi)}${r.oncekiHasar ? '<div class="alt">Önceden hasarlı</div>' : ''}</td><td>${esc(islem)}</td>${fiyat}</tr>`;
  }).join('');

  const emsalSatir = (f.emsaller || []).filter((e) => e.hasarsiz || e.hasarli || e.kaynak).map((e) =>
    `<tr><td>${esc(e.kaynak || '-')}</td><td class="s">${e.hasarsiz ? esc(e.hasarsiz) + ' TL' : '-'}</td><td class="s">${e.hasarli ? esc(e.hasarli) + ' TL' : '-'}</td></tr>`).join('');

  const satir = (k, v, b = false) => `<tr><td>${esc(k)}</td><td class="s${b ? ' b' : ''}">${esc(v)}</td></tr>`;

  const html = `<!doctype html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>Araç Hasar ve Değer Kaybı Hesap Raporu</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
<style>
  @page { size: A4 portrait; margin: 2cm 1.8cm; }
  body { font-family: "Calibri", "Segoe UI", sans-serif; font-size: 10.5pt; color: #1a1a1a; }
  h1 { font-size: 15pt; margin: 0 0 4pt; color: #2b3652; }
  h2 { font-size: 11.5pt; margin: 14pt 0 6pt; color: #2b3652; border-bottom: 1px solid #c9a24a; padding-bottom: 2pt; }
  .ust { font-size: 8.5pt; color: #666; margin: 0 0 12pt; }
  table { border-collapse: collapse; width: 100%; margin-bottom: 8pt; }
  .bilgi td { padding: 3pt 0; border: none; }
  .bilgi .k { width: 36%; color: #555; }
  .bilgi .v { font-weight: bold; }
  .t th { background: #2b3652; color: #fff; font-size: 9pt; padding: 5pt 6pt; text-align: left; border: 1px solid #2b3652; }
  .t td { padding: 5pt 6pt; border: 1px solid #d9dee6; vertical-align: top; }
  .t .s { text-align: right; }
  .t .b { font-weight: bold; }
  .t .alt { font-size: 8.5pt; color: #8a6a1f; }
  .ozet td { padding: 5pt 6pt; border-bottom: 1px solid #e4e4e4; }
  .ozet .s { text-align: right; }
  .ozet .b { font-weight: bold; color: #2b3652; }
  .not { font-size: 8.5pt; color: #555; line-height: 1.5; }
  .not li { margin-bottom: 3pt; }
  .uyari { font-size: 9pt; color: #7a5716; background: #fff6e2; padding: 6pt; }
</style></head>
<body>
  <h1>ARAÇ HASAR VE DEĞER KAYBI HESAP RAPORU</h1>
  <p class="ust">Düzenleme tarihi: ${new Date().toLocaleDateString('tr-TR')} · Bilgilendirme amaçlıdır; bilirkişi raporu yerine geçmez.</p>

  <table class="bilgi">${bilgiSatir}</table>

  <h2>1. Hasar gören parçalar</h2>
  ${parcaSatir ? `<table class="t"><thead><tr><th>Parça</th><th>İşlem</th>${toplamGirisi ? '' : '<th class="s">Parça bedeli (KDV hariç)</th>'}</tr></thead><tbody>${parcaSatir}</tbody></table>` : '<p>-</p>'}

  <h2>2. Maddi hasar (onarım bedeli)</h2>
  ${toplamGirisi ? '<p class="not">Parça fiyatları ayrı ayrı bilinmediğinden hasar, bildirilen parça ve işçilik toplamı üzerinden hesaplanmıştır.</p>' : ''}
  <table class="ozet"><tbody>
    ${toplamGirisi ? `
    ${hasar.birlesikKdvIcinde ? satir('Parça ve işçilik toplamı (KDV dahil bildirilen)', tl(hasar.birlesikGirilen) + ' TL') : ''}
    ${satir('Parça ve işçilik toplamı (KDV hariç)', tl(hasar.birlesik) + ' TL')}
    ${hasar.kiymet ? satir('Kıymet kazanma tenzili', '− ' + tl(hasar.kiymet) + ' TL') : ''}
    ${hasar.kiymet ? satir('Toplam (KDV hariç)', tl(hasar.kdvHaric) + ' TL') : ''}` : `
    ${satir('Parça toplamı', tl(hasar.parcaToplam) + ' TL')}
    ${hasar.iskonto ? satir(`İskonto (%${f.iskonto})`, '− ' + tl(hasar.iskonto) + ' TL') : ''}
    ${hasar.kiymet ? satir('Kıymet kazanma tenzili', '− ' + tl(hasar.kiymet) + ' TL') : ''}
    ${satir('İşçilik toplamı', tl(hasar.iscilikToplam) + ' TL')}
    ${satir('Toplam (KDV hariç)', tl(hasar.kdvHaric) + ' TL')}`}
    ${hasar.kdv ? satir(`KDV (%${f.kdvOrani})`, tl(hasar.kdv) + ' TL') : ''}
    ${satir('TOPLAM HASAR', tl(hasar.toplam) + ' TL', true)}
    ${hasar.kusurOrani < 100 ? satir(`Kusura isabet eden (%${hasar.kusurOrani})`, tl(hasar.kusurluTutar) + ' TL') : ''}
    ${hasar.odenen ? satir('Ödenen', '− ' + tl(hasar.odenen) + ' TL') : ''}
    ${satir('BAKİYE HASAR', tl(hasar.bakiye) + ' TL', true)}
  </tbody></table>

  ${formulle ? `<h2>3. Değer kaybı (ekspertiz verileriyle formülle tahmin)</h2>
  <p class="not">Emsal araştırması girilmediğinden değer kaybı; ekspertiz raporundaki değişen, onarılan ve boyalı parçalar,
  hasar tutarı ve kilometre ile eski Genel Şartlar Ek-1 formülüne (DK = Piyasa Değeri × R × K × H × G) göre tahmin edilmiştir.
  Formül 01.07.2026'da genel şartlardan çıkarılmıştır; mahkeme ve tahkim ikinci el piyasa farkını esas aldığından sonuç tahmin niteliğindedir.</p>
  <table class="ozet"><tbody>
    ${satir(`Piyasa değeri (${dk.piyasaKaynak})`, tl(dk.hasarsiz) + ' TL')}
    ${tahminParcalari ? satir('Hesaba giren parçalar (katsayı)', tahminParcalari) : ''}
    ${satir('R / K', `${et.R.toLocaleString('tr-TR')} / ${et.K.toLocaleString('tr-TR')}`)}
    ${satir('HK / T / H', `${et.HK.toLocaleString('tr-TR')} / ${et.T.toLocaleString('tr-TR', { maximumFractionDigits: 4 })} / ${et.H.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}`)}
    ${satir('G', et.G.toLocaleString('tr-TR'))}
    ${satir('DEĞER KAYBI (TAHMİN)', (f.pert ? '— (araç pert)' : tl(dk.dk) + ' TL'), true)}
    ${satir('Değer kaybı oranı', yuzde(dk.dkOrani))}
    ${dk.kusurOrani < 100 ? satir(`Kusura isabet eden (%${dk.kusurOrani})`, tl(dk.kusurluTutar) + ' TL') : ''}
    ${dk.odenen ? satir('Ödenen', '− ' + tl(dk.odenen) + ' TL') : ''}
    ${satir('BAKİYE DEĞER KAYBI', tl(f.pert ? 0 : dk.bakiye) + ' TL', true)}
  </tbody></table>` : `<h2>3. Değer kaybı (ikinci el piyasa araştırması)</h2>
  ${emsalSatir ? `<table class="t"><thead><tr><th>Kaynak</th><th class="s">Hasarsız</th><th class="s">Onarılmış</th></tr></thead><tbody>${emsalSatir}</tbody></table>` : ''}
  <table class="ozet"><tbody>
    ${dk.endeks !== 1 ? satir('Kaza tarihine indirgeme katsayısı (TÜFE)', dk.endeks.toLocaleString('tr-TR', { maximumFractionDigits: 4 })) : ''}
    ${satir('Hasarsız ikinci el rayiç (ortalama)', tl(dk.hasarsiz) + ' TL')}
    ${satir('Onarılmış ikinci el rayiç (ortalama)', tl(dk.hasarli) + ' TL')}
    ${satir('DEĞER KAYBI', (f.pert ? '— (araç pert)' : tl(dk.dk) + ' TL'), true)}
    ${dk.hasarsiz ? satir('Değer kaybı oranı / hasar oranı', `${yuzde(dk.dkOrani)} / ${yuzde(dk.hasarOrani)}`) : ''}
    ${dk.kusurOrani < 100 ? satir(`Kusura isabet eden (%${dk.kusurOrani})`, tl(dk.kusurluTutar) + ' TL') : ''}
    ${dk.odenen ? satir('Ödenen', '− ' + tl(dk.odenen) + ' TL') : ''}
    ${satir('BAKİYE DEĞER KAYBI', tl(f.pert ? 0 : dk.bakiye) + ' TL', true)}
  </tbody></table>`}

  ${karsilastirma ? `<h2>4. Karşılaştırma: eski Genel Şartlar Ek-1 formülü</h2>
  <p class="not">01.07.2026'da yürürlükten kaldırılan formül (DK = Piyasa Değeri × R × K × H × G); yalnızca karşılaştırma içindir, mahkemeyi bağlamaz (AYM 17.07.2020, 2019/40 E.).</p>
  <table class="ozet"><tbody>
    ${satir(`Piyasa değeri (${ek1.piyasaKaynak})`, tl(ek1.piyasa) + ' TL')}
    ${satir('R / K', `${ek1.R.toLocaleString('tr-TR')} / ${ek1.K.toLocaleString('tr-TR')}`)}
    ${satir('HK / T / H', `${ek1.HK.toLocaleString('tr-TR')} / ${ek1.T.toLocaleString('tr-TR', { maximumFractionDigits: 4 })} / ${ek1.H.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}`)}
    ${satir('G', ek1.G.toLocaleString('tr-TR'))}
    ${satir('Eski formülle değer kaybı', tl(ek1.DK) + ' TL', true)}
  </tbody></table>` : ''}

  ${f.mahrumiyetAcik ? `<h2>${karsilastirma ? '5' : '4'}. Araç mahrumiyeti (trafik sigortası teminatı dışında)</h2>
  <table class="ozet"><tbody>
    ${satir('Net günlük ikame bedeli', tl(mahrumiyet.net) + ' TL')}
    ${satir('Makul onarım süresi', `${f.onarimGun || 0} gün`)}
    ${satir('ARAÇ MAHRUMİYETİ', tl(mahrumiyet.tutar) + ' TL', true)}
  </tbody></table>` : ''}

  <h2>Sonuç</h2>
  <table class="ozet"><tbody>
    ${satir('Sigortadan istenebilecek (poliçe limiti dahilinde)', tl(ozet.sigortadan) + ' TL', true)}
    ${ozet.isletenden ? satir('Sürücü / işleten / malikten', tl(ozet.isletenden) + ' TL', true) : ''}
    ${temerrut ? satir('Sigortacının 8 iş günlük süresinin dolduğu tarih (KTK m.99)', tarihTR(temerrut)) : ''}
    ${zamanasimi10 ? satir('Azami zamanaşımı (kazadan 10 yıl, KTK m.109)', tarihTR(zamanasimi10)) : ''}
  </tbody></table>
  ${uyarilar.length ? `<div class="uyari">${uyarilar.map((u) => esc(u.m)).join('<br>')}</div>` : ''}

  <h2>Hesap esasları ve dayanaklar</h2>
  <ul class="not">${DAYANAKLAR.map((d) => `<li><b>${esc(d.baslik)}:</b> ${esc(d.metin)}</li>`).join('')}</ul>
</body></html>`;

  const blob = new Blob(['﻿', html], { type: 'application/msword' });
  const dosyaAdi = `arac-hasar-deger-kaybi-${bilgi.foyNo || (f.plaka || 'hesap').replace(/\s+/g, '')}-${bugunISO()}.doc`;
  return { blob, dosyaAdi };
}
